'use client'

// Verification badge component
// Displays user verification status with appropriate styling
import { User } from '@/types'
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react'

interface VerificationBadgeProps {
  user: User
  showText?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export default function VerificationBadge({ user, showText = true, size = 'md' }: VerificationBadgeProps) {
  // Get verification status
  const getVerificationStatus = () => {
    // Admin users are always verified
    if (user.role === 'admin') {
      return {
        status: 'verified',
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        text: 'Verified',
        icon: CheckCircle2,
        iconColor: 'text-emerald-600'
      }
    }
    
    // Check if user is verified
    if (user.is_verified) {
      return {
        status: 'verified',
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        text: 'Verified',
        icon: CheckCircle2,
        iconColor: 'text-emerald-600'
      }
    }
    
    // Check KYC status - only show pending if backend explicitly says so
    const kycStatus = user.kyc?.status || user.kyc_data?.verification_status
    
    // Check if user has rejected KYC
    if (kycStatus === 'rejected') {
      return {
        status: 'rejected',
        color: 'bg-red-100 text-red-800 border-red-200',
        text: 'Rejected',
        icon: AlertCircle,
        iconColor: 'text-red-600'
      }
    }
    
    // Only show pending if backend status is explicitly 'pending' or 'in_review'
    // This means user has applied for KYC and it's under review
    if (kycStatus === 'pending' || kycStatus === 'in_review') {
      return {
        status: 'pending',
        color: 'bg-amber-100 text-amber-800 border-amber-200',
        text: 'Pending',
        icon: Clock,
        iconColor: 'text-amber-600'
      }
    }
    
    // If status is null/undefined, user has not applied for KYC yet
    // Show "KYC Needed" with red indicator
    return {
      status: 'kyc_needed',
      color: 'bg-red-100 text-red-800 border-red-200',
      text: 'KYC Needed',
      icon: AlertCircle,
      iconColor: 'text-red-600'
    }
  }

  const verificationStatus = getVerificationStatus()
  const Icon = verificationStatus.icon

  // Size classes
  const sizeClasses = {
    sm: 'px-2.5 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  }

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full border-2 ${verificationStatus.color} ${sizeClasses[size]} font-semibold`}>
      <Icon className={`${iconSizes[size]} ${verificationStatus.iconColor}`} />
      {showText && <span>{verificationStatus.text}</span>}
    </div>
  )
}
